import {
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
  useContext,
  useRef,
} from "react";
import axios from "axios";
import TkDialog from "../TkDialog";
import { GlobalContext } from "../../../context/globalContext";
import authorityUserType from "../../../assets/data/TkAuthorityProperty";
import { Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

const TkVersionChecker = forwardRef((props, ref) => {
  // グローバルコンテキストからstateを取得
  const { state } = useContext(GlobalContext);
  // ダイアログを開くための状態管理
  const [open, setOpen] = useState(false);
  const [openSPMask, setOpenSPMask] = useState(false);
  const [openMask, setOpenMask] = useState(false);

  // 特定のパスに対して特別な処理が必要な画面のリスト
  const needOpenSpMask = useRef(["/SCRC010001", "/SCRC010002", "/SCRC030001"]);
  const needOpenMask = useRef(["/SCRR410001", "/SCRR010002", "/SCRR010001"]);
  const needToCheckUserType = useRef(["/SCRC010003"]);

  const isFirstLoad = useRef(true);

  // 現在のパスを取得
  const location = useLocation();
  const path = useRef<any>(location.pathname);

  useEffect(() => {
    path.current = location.pathname;
  }, [location.pathname]);

  // バージョンチェック用のタイマーリファレンス
  const intervalRef = useRef<any>(null);

  const baseURL = process.env.REACT_APP_BASE_NAME || "";

  // サーバーからバージョン番号を取得する関数
  const getVersionFromServer = async () => {
    try {
      const response = await axios.get(`${baseURL}/version.json`, {
        params: { t: new Date().getTime() }, // キャッシュを防止するためのタイムスタンプを付与
      });
      return response.data.version;
    } catch (error) {
      console.error("バージョン番号の取得に失敗しました:", error);
      return null;
    }
  };

  // バージョン変更時の処理を行う関数
  const handleVersionChange = () => {
    if (needOpenSpMask.current.includes(path.current)) {
      setOpenSPMask(true);
      return;
    }

    if (needOpenMask.current.includes(path.current)) {
      setOpenMask(true);
      return;
    }

    if (needToCheckUserType.current.includes(path.current)) {
      if (state.authInfo?.userType) {
        setOpen(true);
      } else {
        setOpenMask(true);
      }
      return;
    }

    const userType: any = state.authInfo?.userType ?? "";
    const maskMap = [
      authorityUserType.userTypeNumber.TAISYOKUSYA,
      authorityUserType.userTypeNumber.BENGOSHI,
      authorityUserType.userTypeNumber.DAIKOSHA,
    ];

    if (maskMap.includes(userType)) {
      setOpenMask(true);
      return;
    }

    setOpen(true);
  };

  // バージョン番号を取得し、変更があれば処理を行う関数
  const fetchVersion = useCallback(async () => {
    const newVersion = await getVersionFromServer();
    if (!newVersion) return;

    const storedVersion = localStorage.getItem("tkapp.VersionChecker.version");
    if (isFirstLoad.current) {
      localStorage.setItem("tkapp.VersionChecker.version", newVersion);
      isFirstLoad.current = false;
      return;
    }

    if (storedVersion && newVersion !== storedVersion) {
      handleVersionChange();
    }

    // 新しいバージョン番号をローカルストレージに保存
    localStorage.setItem("tkapp.VersionChecker.version", newVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.authInfo?.userType, path]);

  // コンポーネントのマウント時に一度だけバージョン番号をチェックし、その後60分ごとにポーリング
  useEffect(() => {
    fetchVersion();
    intervalRef.current = setInterval(() => {
      fetchVersion();
    }, 3600000); // 60分ごとにポーリング 3600000

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 親コンポーネントからバージョンチェックを実行するためのハンドル
  useImperativeHandle(ref, () => ({
    checkVersion: fetchVersion,
  }));

  // ダイアログの「ログイン画面へ」ボタンをクリックしたときの処理
  const handleReload = () => {
    window.location.href = `${baseURL}/SCRC010001`;
  };

  // ダイアログの「確認」ボタンをクリックしたときの処理
  const handleReloadForSP = () => {
    window.location.reload();
  };

  return (
    <>
      <TkDialog
        open={open}
        onSave={handleReload}
        okText="ログイン画面へ"
        cancelText=""
      >
        <Typography>システムメンテナンスが完了しました。</Typography>
        <Typography>ログイン画面より、再度ログインしてください。</Typography>
      </TkDialog>

      <TkDialog
        open={openSPMask}
        onSave={handleReloadForSP}
        okText="確認"
        cancelText=""
      >
        <Typography>システムメンテナンスが完了しました。</Typography>
        <Typography>画面の再表示を行います。</Typography>
      </TkDialog>

      <TkDialog open={openMask} okText="" cancelText="">
        <Typography>システムメンテナンスが完了しました。</Typography>
        <Typography>
          ブラウザのタブを閉じて、メールに記載されているリンクから再度ログインをしてください。
        </Typography>
      </TkDialog>
    </>
  );
});

export default TkVersionChecker;
